/**
 * Stores a card poster the client made (an HTML page's capture, a PDF's
 * first page): uploads the image, stores it for one theme with its cover
 * descriptor and patches the frame in the store.
 */
import type { ApiError } from "@/api/client";
import {
  putFrameThumbnail,
  type FrameThumbnailUpload,
} from "@/api/frame-listing";
import { uploadImage } from "@/api/uploads";
import { applyFramePatch } from "@/lib/frame-ingest";
import { useFramesStore } from "@/stores/frames-store";

/**
 * How a poster ended: `"stored"`; `"rejected"` for good (no usable image,
 * or the server refused it, e.g. 409 when the source changed meanwhile);
 * `"retry"` when it may work later (no answer, a network or server failure).
 */
export type PosterOutcome = "stored" | "rejected" | "retry";

/** The outcome of a poster that threw: a 4xx is final, the rest may work later. */
export function posterFailure(error: unknown): PosterOutcome {
  const status = (error as Partial<ApiError> | null)?.status;
  return typeof status === "number" && status >= 400 && status < 500
    ? "rejected"
    : "retry";
}

/** Uploads `file` and stores it as `frameId`'s poster; throws on failure. */
export async function storeFramePoster(
  projectId: string,
  frameId: string,
  file: File,
  upload: Omit<FrameThumbnailUpload, "imageId">,
): Promise<void> {
  const uploaded = await uploadImage(file);
  const frame = await putFrameThumbnail(projectId, frameId, {
    ...upload,
    imageId: uploaded.id,
  });
  const current = useFramesStore.getState().framesById[frameId];
  if (current) {
    applyFramePatch(frameId, {
      metadata: { ...current.metadata, thumbnail: frame.metadata?.thumbnail },
    });
  }
}
